import { useEffect, useState } from "react";

const SCREEN_EDGE_TOLERANCE_PX = 24;
const ANCHOR_POLL_INTERVAL_MS = 1000;
// Windows parks minimized windows at -32000,-32000; anything this far
// off-screen is a placeholder, not a real position. Regular multi-monitor
// negative origins (a monitor left of the primary) stay well above this.
const MINIMIZED_COORDINATE_PX = -30000;

export type AnchorHostWindow = Pick<
	Window,
	"screenX" | "screenY" | "outerWidth" | "outerHeight"
> & {
	screen: Pick<Screen, "availWidth" | "availHeight"> & {
		availLeft?: number;
		availTop?: number;
	};
};

// availLeft/availTop are Chromium-only (fine for Teams desktop and Edge/Chrome);
// they resolve the current monitor's work-area origin in multi-monitor setups.
// The host parameter exists for unit testing; production code uses the default.
export const isWindowAnchoredBottomRight = (
	host: AnchorHostWindow = window
) => {
	const { screen } = host;

	const workAreaRight = (screen.availLeft ?? 0) + screen.availWidth;
	const workAreaBottom = (screen.availTop ?? 0) + screen.availHeight;
	const windowRight = host.screenX + host.outerWidth;
	const windowBottom = host.screenY + host.outerHeight;

	return (
		Math.abs(workAreaRight - windowRight) <= SCREEN_EDGE_TOLERANCE_PX &&
		Math.abs(workAreaBottom - windowBottom) <= SCREEN_EDGE_TOLERANCE_PX
	);
};

export const isWindowMinimized = (host: AnchorHostWindow = window) =>
	host.screenX <= MINIMIZED_COORDINATE_PX ||
	host.screenY <= MINIMIZED_COORDINATE_PX;

// True while the host window's bottom-right corner sits on the screen's
// work-area corner (maximized, or snapped to the right half) — i.e. while the
// Teams desktop call toast overlaps the app's bottom-right region.
// Pass enabled=false where the result is not used (e.g. Teams web/mobile) to
// skip the resize listener and the poll entirely; the hook then returns false.
export function useWindowAnchoredBottomRight(enabled: boolean = true): boolean {
	// While minimized the window reports placeholder coordinates, so there is
	// no real position to sample at mount; assume anchored (restores mostly
	// land on maximized windows) and correct on the restore's visibilitychange.
	const [isAnchored, setIsAnchored] = useState(
		() => enabled && (isWindowMinimized() || isWindowAnchoredBottomRight())
	);

	useEffect(() => {
		if (!enabled) {
			setIsAnchored(false);
			return undefined;
		}

		const update = () => {
			// Skip placeholder samples while minimized — flipping to "not
			// anchored" would corner-dock the card and make it jump back to
			// the toast position right after the window is restored.
			if (isWindowMinimized()) {
				return;
			}

			setIsAnchored(isWindowAnchoredBottomRight());
		};

		update();

		// Moving the window without resizing it (e.g. Win+Shift+arrow between
		// monitors) fires no resize event, so poll while mounted as a fallback.
		// Restoring a maximized window fires no resize either, but it flips
		// visibility and focus — react to those to re-position immediately.
		window.addEventListener("resize", update);
		window.addEventListener("focus", update);
		document.addEventListener("visibilitychange", update);
		const pollHandle = window.setInterval(update, ANCHOR_POLL_INTERVAL_MS);

		return () => {
			window.removeEventListener("resize", update);
			window.removeEventListener("focus", update);
			document.removeEventListener("visibilitychange", update);
			window.clearInterval(pollHandle);
		};
	}, [enabled]);

	return isAnchored;
}

export default function useResizeListener() {
	const [windowSize, setWindowSize] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	useEffect(() => {
		const handleResize = () => {
			setWindowSize({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		};

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	return windowSize;
}
